<template>
  <el-dialog
    :title="'成长趋势 — ' + (studentName || '')"
    :visible.sync="dialogVisible"
    width="640px"
    top="6vh"
    custom-class="fe-growth-dialog"
    append-to-body
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div v-loading="loading" class="fe-growth-dialog__body">
      <div v-if="overallStar != null" class="fe-growth-dialog__summary">
        <span class="fe-growth-dialog__summary-label">当前综合星级</span>
        <StarDisplay :value="overallStar" :max="5" />
      </div>

      <div
        v-for="section in sections"
        :key="section.l1Id"
        class="fe-growth-section"
      >
        <div class="fe-growth-section__head">
          <span class="fe-growth-section__title">{{ section.l1Name }}</span>
          <StarDisplay :value="section.displayStar" :max="5" size="small" />
        </div>

        <div v-if="section.l2Tabs.length" class="fe-growth-l2-tabs">
          <button
            v-for="tab in section.l2Tabs"
            :key="tab.key"
            type="button"
            class="fe-growth-l2-tab"
            :class="{ 'fe-growth-l2-tab--active': section.activeL2Key === tab.key }"
            @click="switchL2Tab(section, tab)"
          >{{ tab.label }}</button>
        </div>

        <FeStarTrendChart :items="section.chartItems" height="200px" />
        <div v-if="!loading && !section.chartItems.length" class="fe-growth-empty">
          暂无历史星级数据
        </div>
      </div>

      <div v-if="!loading && !sections.length" class="fe-growth-empty">
        暂无可展示的成长趋势
      </div>
    </div>
  </el-dialog>
</template>

<script>
import StarDisplay from "@/components/StarDisplay";
import FeStarTrendChart from "./FeStarTrendChart.vue";
import { getGrowthTrend, getGrowthTrendByL2 } from "@/api/fiveEducation";

/**
 * 成长趋势弹窗：按一级分区，二级 Tab 切换折线图（Y 轴为星级 1~5）
 */
export default {
  name: "FeGrowthTrendDialog",
  components: { StarDisplay, FeStarTrendChart },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    studentId: {
      type: [Number, String],
      default: null,
    },
    studentName: {
      type: String,
      default: "",
    },
    overallStar: {
      type: Number,
      default: null,
    },
    /** 学生详情 Categories（含 SubCategories） */
    categories: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data: function () {
    return {
      loading: false,
      sections: [],
      chartCache: {},
    };
  },
  computed: {
    dialogVisible: {
      get: function () {
        return this.visible;
      },
      set: function (val) {
        this.$emit("update:visible", val);
      },
    },
  },
  methods: {
    handleOpen: function () {
      this.initSections();
      this.loadAllCharts();
    },

    handleClosed: function () {
      this.sections = [];
      this.chartCache = {};
    },

    /** 根据 Categories 初始化分区与二级 Tab */
    initSections: function () {
      var self = this;
      this.sections = (this.categories || []).map(function (l1) {
        var l2List = l1.SubCategories || [];
        var l2Tabs = [{ key: "l1_" + l1.CategoryL1Id, label: "综合", l2Id: 0 }];
        l2List.forEach(function (l2) {
          l2Tabs.push({
            key: "l2_" + l2.CategoryL2Id,
            label: l2.CategoryName,
            l2Id: l2.CategoryL2Id,
          });
        });
        return {
          l1Id: l1.CategoryL1Id,
          l1Name: l1.CategoryName,
          displayStar: l1.DisplayStar,
          l2Tabs: l2Tabs,
          activeL2Key: l2Tabs[0].key,
          chartItems: [],
        };
      });
    },

    cacheKey: function (l1Id, l2Id) {
      return String(this.studentId) + "_" + l1Id + "_" + (l2Id || 0);
    },

    /** 加载全部分区默认图表 */
    loadAllCharts: async function () {
      if (!this.studentId || !this.sections.length) return;
      this.loading = true;
      try {
        var tasks = this.sections.map(function (section) {
          return this.loadSectionChart(section);
        }.bind(this));
        await Promise.all(tasks);
      } finally {
        this.loading = false;
      }
    },

    loadSectionChart: async function (section) {
      var tab = section.l2Tabs.find(function (t) {
        return t.key === section.activeL2Key;
      });
      if (!tab) return;

      var key = this.cacheKey(section.l1Id, tab.l2Id);
      if (this.chartCache[key]) {
        section.chartItems = this.chartCache[key];
        return;
      }

      var res;
      if (tab.l2Id > 0) {
        res = await getGrowthTrendByL2(this.studentId, tab.l2Id);
      } else {
        res = await getGrowthTrend(this.studentId, section.l1Id);
      }

      var items = res.data && res.data.success ? (res.data.response || []) : [];
      this.chartCache[key] = items;
      section.chartItems = items;
    },

    switchL2Tab: async function (section, tab) {
      if (section.activeL2Key === tab.key) return;
      section.activeL2Key = tab.key;
      this.loading = true;
      try {
        await this.loadSectionChart(section);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
